import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProcessBacklogResponse, StartRecordsResponse } from '@aica/api-types';
import { useToast } from '@/contexts/ToastContext';

/**
 * Чем закончился запуск накопленного — вслух.
 *
 * Появилось потому, что не говорилось ничего: запуск, отвергнутый пайплайном
 * на каждой записи, возвращал `queued: 0` со статусом 200, экран инвалидировал
 * запросы и перерисовывал ту же карточку с тем же числом. Для клиента это
 * выглядело как кнопка, которая не работает, — и выглядело так неделями (#400).
 *
 * Один хук на обе поверхности: в портале и внутри Bitrix24 состояние собирают
 * разные хуки (Clerk против cookie-сессии), но говорить о результате они
 * обязаны одинаково. `ToastContextProvider` смонтирован в обеих ветках
 * `__root.tsx`.
 */
export function useBacklogResultToast() {
  const { toast } = useToast();
  const { t } = useTranslation('records');

  const onSuccess = useCallback(
    (result: ProcessBacklogResponse) => {
      // Ничего не ушло и нечему было уходить — не отказ.
      //
      // Так бывает буднично: выбрано окно «за сегодня», а сегодня записей нет;
      // или второй администратор нажал кнопку секундой раньше. Прежнее условие
      // смотрело только на `queued === 0` и выдавало «Запустить не удалось
      // ничего. Отклонено разговоров: 0» — сообщение о провале, которого не
      // было, да ещё и с нулём в качестве числа отказов.
      // Отказ приёмника закрытой обработкой источника (#2709) — тоже
      // не запущенная запись, а не «пусто»: пауза легла между отбором и
      // запуском, и без причины человек нажимал бы снова.
      const notQueued = result.failed + result.processingPaused;
      const pausedNote =
        result.processingPaused > 0
          ? t('records.start.resultProcessingPausedNote', {
              count: result.processingPaused,
            })
          : null;

      if (result.queued === 0 && notQueued === 0) {
        toast.info(t('records.backlog.resultEmptyTitle'));
        return;
      }

      // Ничего не ушло — это отказ, а не «готово». Молчание здесь и было багом.
      if (result.queued === 0) {
        toast.error(
          t('records.backlog.resultNoneTitle'),
          [
            t('records.backlog.resultNoneBody', {
              failed: notQueued,
              count: notQueued,
            }),
            pausedNote,
          ]
            .filter(Boolean)
            .join(' ')
        );
        return;
      }

      // Партия ограничена потолком одного запуска: остаток называется, иначе
      // «отправлено 200» читается как «отправлено всё» (code review r2 на
      // #1454 — сервер говорил `remaining`, интерфейс его не читал).
      const remainingNote =
        result.remaining === 'more' ? t('records.backlog.resultRemainingBody') : null;

      // Ушло не всё: называем обе половины, иначе «отправлено 40» скрывает,
      // что 28 записей остались лежать ровно там, где лежали.
      if (notQueued > 0) {
        toast.info(
          t('records.backlog.resultOkTitle', {
            queued: result.queued,
            count: result.queued,
          }),
          [
            t('records.backlog.resultPartialBody', {
              failed: notQueued,
              count: notQueued,
            }),
            pausedNote,
            remainingNote,
          ]
            .filter(Boolean)
            .join(' ')
        );
        return;
      }

      const title = t('records.backlog.resultOkTitle', {
        queued: result.queued,
        count: result.queued,
      });
      if (remainingNote) toast.info(title, remainingNote);
      else toast.success(title);
    },
    [toast, t]
  );

  /**
   * Чем закончился ручной запуск ВЫБРАННЫХ записей.
   *
   * Своих три состояния, а не четыре, как у бэклога: `recordIds` там непустой
   * по схеме (`min(1)`), значит «нечего было запускать» здесь не бывает — сет
   * либо ушёл, либо отклонён.
   */
  const onStartSuccess = useCallback(
    (result: StartRecordsResponse) => {
      // Отклонённые пайплайном и пропущенные (чужие, не в запускаемом статусе,
      // уже забранные параллельным запуском) называем вместе: и то и другое —
      // запись, которая осталась НЕ запущенной этим нажатием, а «запущено 2 из
      // 5» без остатка скрывает, что 3 записи не тронуты.
      const notQueued = result.failed + result.skipped + result.processingPaused;
      // Записи источника, чья обработка остановлена (#2709), — с причиной:
      // без неё человек нажимал бы снова, а запись не запустится, пока
      // обработку не возобновят.
      const pausedNote =
        result.processingPaused > 0
          ? t('records.start.resultProcessingPausedNote', {
              count: result.processingPaused,
            })
          : '';

      // Ничего не запустилось — отказ, а не тишина: клиент явно выбрал записи,
      // и молчание здесь читалось бы как та же неработающая кнопка (#400).
      if (result.queued === 0) {
        toast.error(
          t('records.start.resultNoneTitle'),
          [t('records.start.resultNoneBody', { notQueued, count: notQueued }), pausedNote]
            .filter(Boolean)
            .join(' ')
        );
        return;
      }

      if (notQueued > 0) {
        toast.info(
          t('records.start.resultOkTitle', {
            queued: result.queued,
            count: result.queued,
          }),
          [
            t('records.start.resultPartialBody', {
              notQueued,
              count: notQueued,
            }),
            pausedNote,
          ]
            .filter(Boolean)
            .join(' ')
        );
        return;
      }

      toast.success(
        t('records.start.resultOkTitle', {
          queued: result.queued,
          count: result.queued,
        })
      );
    },
    [toast, t]
  );

  /**
   * Отказ, который можно исправить, называется по имени.
   *
   * 403 — прав не хватает, и повторять бессмысленно. Общее «повторите, если
   * повторится — напишите в поддержку» отправляло клиента в поддержку за
   * тумблером, до которого он мог дотянуться сам.
   *
   * 409 здесь не разбирается. Прежний 409 — код гейта «анализ выключен»,
   * который process-backlog отвечал ДО того, как ручной режим стал законным
   * способом запускать накопленное, — маршрут его больше не отдаёт. Нынешний
   * 409 «цена выросла» (#1396) сюда не доходит: его объявляет тот, кто
   * показывает новую цену (`onPriceChanged` ниже), а мимо этого обработчика его
   * проводят хуки состояния запуска (`useProcessingState`).
   */
  const onError = useCallback(
    (error: unknown) => {
      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? (error as { status?: number }).status
          : undefined;
      // Тело ответа лежит в `ApiError.data`, а не в поле `code` самой ошибки
      // (`fetch-utils.ts`: «Attach parsed error data to ApiError»). Чтение
      // верхнего уровня давало `undefined` ВСЕГДА — то есть настоящий отказ по
      // правам подписывался как устаревшая сессия, ровно наоборот к тому, что
      // эта ветка чинила. Верхний уровень оставлен вторым источником: им
      // пользуются ошибки, собранные не из ответа.
      const errorObject = (error ?? {}) as {
        code?: string;
        data?: { code?: string } | null;
      };
      const code = errorObject.data?.code ?? errorObject.code;

      // 403 приходит от ТРЁХ разных источников. Два серверных гейта отвечают
      // им, когда роли не хватает — `process-backlog` кодом
      // BACKLOG_FORBIDDEN_ROLE, ручной запуск выбранных (`/records/start`) —
      // START_FORBIDDEN_ROLE; во встроенном режиме тем же статусом отвечает
      // ещё и CSRF-гейт (`embedded-session.ts`), когда токен сессии протух
      // или потерялся. Пропустить один из двух кодов роли — та же ошибка
      // наоборот, от которой это различение и появилось: настоящий отказ по
      // правам подписывается как устаревшая сессия, а совет («обновите
      // страницу») звучит ровно противоположный тому, что нужен владельцу.
      // Цену запуска нечем посчитать (биллинг не ответил) — сервер не запустил
      // ничего, а не «что-то сломалось»: причина и совет свои (code review r2
      // на #1454).
      if (code === 'START_PRICE_UNAVAILABLE') {
        toast.error(
          t('records.start.priceUnavailableTitle'),
          t('records.start.priceUnavailableBody')
        );
        return;
      }

      if (status === 403) {
        if (code === 'BACKLOG_FORBIDDEN_ROLE' || code === 'START_FORBIDDEN_ROLE') {
          toast.error(t('records.backlog.resultForbiddenTitle'));
        } else {
          toast.error(
            t('records.backlog.resultStaleSessionTitle'),
            t('records.backlog.resultStaleSessionBody')
          );
        }
        return;
      }

      toast.error(
        t('records.backlog.resultErrorTitle'),
        t('records.backlog.resultErrorBody')
      );
    },
    [toast, t]
  );

  /**
   * Отказ переключения режима — того же тумблера, что показывают баннер
   * записей и настройки организации через один и тот же `useProcessingState`.
   *
   * Без обработчика мутация проваливалась молча: TanStack Query откатывает
   * выбранную радиокнопку назад к прежнему режиму, а человек не видит, ПОЧЕМУ
   * — вернувшийся автоматический режим выглядит как баг интерфейса, а не как
   * отказ сервера.
   */
  const onModeError = useCallback(() => {
    toast.error(
      t('records.processingOff.switchFailedTitle'),
      t('records.processingOff.switchFailedBody')
    );
  }, [toast, t]);

  /**
   * Запуск не состоялся: цена выросла, пока окно подтверждения было открыто
   * (#1396). Не сбой — ничего не списано. Зовёт только тот, кто уже показал
   * новую цену (диалог открыт снова, карточка перечитана): иначе тост
   * «проверьте новую цену» указывал бы на цену, которой на экране нет.
   */
  const onPriceChanged = useCallback(() => {
    toast.info(t('records.start.priceChangedTitle'), t('records.start.priceChangedBody'));
  }, [toast, t]);

  return { onSuccess, onStartSuccess, onError, onModeError, onPriceChanged };
}
